import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { NAV_BADGES_KEY } from "@/hooks/useNavBadges";

export interface RatingSummary {
  count: number;
  quality: number | null;
  cooperation: number | null;
  timeliness: number | null;
  overall: number | null;
  in_progress_orders: number;
}

export interface ContractorRating {
  id: string;
  order: string;
  order_number: string;
  /** Wartości netto pozycji ocenionego zamówienia; null = brak uprawnienia do stawki.
   *  Sumuje je VisibleAmountSum — nigdy nie sumować ręcznie, bo zgubi się gwiazdka
   *  informująca, że część kwot jest ukryta. */
  order_line_amounts: (string | null)[];
  quality: number;
  cooperation: number;
  timeliness: number;
  average: number;
  comment: string;
  rated_by_name: string | null;
  created_at: string;
}

export interface AwaitingRating {
  id: string;
  order_number: string;
  status: string;
}

export interface ContractorRatings {
  summary: RatingSummary;
  ratings: ContractorRating[];
  awaiting_rating: AwaitingRating[];
}

export interface RatingPayload {
  quality: number;
  cooperation: number;
  timeliness: number;
  comment: string;
}

export const RATING_AXES = [
  { key: "quality", label: "Jakość wykonania" },
  { key: "cooperation", label: "Płynność współpracy" },
  { key: "timeliness", label: "Czas wykonania" },
] as const;

export function useContractorRatings(contractorId: string | undefined) {
  return useQuery({
    queryKey: ["rfq", "ratings", contractorId],
    enabled: !!contractorId,
    queryFn: () =>
      apiClient
        .get<{ data: ContractorRatings }>(`/rfq/contractors/${contractorId}/ratings/`)
        .then((r) => r.data.data),
  });
}

export interface OrderRatingState {
  rating: ContractorRating | null;
  can_rate: boolean;
  is_rateable: boolean;
}

export function useOrderRating(orderId: string | undefined) {
  return useQuery({
    queryKey: ["rfq", "order-rating", orderId],
    enabled: !!orderId,
    queryFn: () =>
      apiClient
        .get<{ data: OrderRatingState }>(`/rfq/orders/${orderId}/rating/`)
        .then((r) => r.data.data),
  });
}

export function useSaveOrderRating(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RatingPayload) =>
      apiClient.put(`/rfq/orders/${orderId}/rating/`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rfq", "order-rating", orderId] });
      qc.invalidateQueries({ queryKey: ["rfq", "ratings"] });
      // Czarny badge „do oceny" i baner nad listą zamówień mają zniknąć od razu
      // po wystawieniu oceny, a nie dopiero po kolejnym pollingu nav-badge'ów.
      qc.invalidateQueries({ queryKey: NAV_BADGES_KEY });
      qc.invalidateQueries({ queryKey: ["orders", "feed"] });
    },
  });
}

export function useRateOrder(contractorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, ...payload }: RatingPayload & { orderId: string }) =>
      apiClient.put(`/rfq/orders/${orderId}/rating/`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rfq", "ratings", contractorId] });
      qc.invalidateQueries({ queryKey: NAV_BADGES_KEY });
    },
  });
}
